/**
 * ATL-1445 stage 3, P0 — the `firestore-only-writer-usage` gate clause's evidence.
 *
 * Event: `firestore_only_writer_used`, emitted by every writer in `FIRESTORE_ONLY_WRITERS` when it is
 * about to write. After an organization's write cutover these writers either stop existing (their
 * write is routed through the backend, converted, or disabled for a cut-over organization) or keep
 * writing a store that is no longer the master of anything they touch. The clause in
 * `atlas-api-v1-write-cutover.md` §5 needs to know, per organization and over 30 days, which of them
 * that organization still uses — "most of these writers emit no event today", so until this shipped
 * the clause could only ever read `unverifiable`.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE CONTRACT (mirrored in the design doc §5 "Telemetry contract"; change both or neither)
 *
 *   event                    'firestore_only_writer_used'
 *   writer                   a key of FIRESTORE_ONLY_WRITERS — stable; a rename is a new writer
 *   organization_id          the organization the write targets (Firestore id = PostHog `organization`
 *                            group key). Explicit, rather than left to `person.properties.
 *                            organizationId`, because the writer knows the org it is writing to and a
 *                            person property can lag an account switch. Null only when neither the
 *                            caller nor the session knows it — the gate must count such a row as
 *                            unattributable, never drop it.
 *   writer_registry_version  FIRESTORE_ONLY_WRITER_REGISTRY_VERSION of the emitting bundle
 *   writer_target            OPTIONAL, a non-identifying enum-ish detail (the collection name a
 *                            reset clears); absent for most writers
 *   (+ super properties)     update_id, platform, js_version, web_build_at … as on every event
 *
 * The gate keys its policy on `writer`, NEVER on anything the bundle says about the writer's fate:
 * the disposition below is this repo's CURRENT plan and it will change, while an old bundle keeps
 * emitting for years. That is why `disposition` is not an event property.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHEN IT FIRES
 *
 * Immediately BEFORE the Firestore/Storage write, after any guard that decides no write happens
 * (a validation that finds nothing to fix emits nothing; `deleteProductImage` with no name emits
 * nothing). Before, not after: a write that then fails is still an organization relying on a writer
 * that will not exist, and emitting after a throw would hide exactly the flaky usage the clause most
 * needs to see. The reset writers report at ENTRY, before their read: a person ran the reset, which
 * is the feature the clause asks about, whether or not any document matched the cutoff.
 *
 * `trigger: 'user'` writers emit on EVERY call — they are rare and each one is a person using a
 * feature. `trigger: 'automatic'` writers (the derived-cache validators that run on their own,
 * several times a day, on every till) emit at most once per (writer, organization, UTC day) per JS
 * process. The clause asks WHETHER an organization used a writer in 30 days, not how often, and an
 * automatic writer's count is a function of how many tills were open, not of anything a person chose.
 * The bound is stated here so nobody reads the daily count as a call count.
 *
 * Never throws, and cannot change what the writer does: it is one synchronous call that only ever
 * enqueues an event.
 */
import { getDefaultStore } from 'jotai'
import { organizationIdAtom } from '../AtomsProvider'
import { captureOperationalEvent, type OperationalProperties } from './operationalTelemetry'
import {
  FIRESTORE_ONLY_WRITERS,
  FIRESTORE_ONLY_WRITER_EVENT,
  FIRESTORE_ONLY_WRITER_REGISTRY_VERSION,
  type FirestoreOnlyWriterEntry,
  type FirestoreOnlyWriterName,
} from './firestoreOnlyWriterRegistry'

export * from './firestoreOnlyWriterRegistry'

/** (writer|org|UTC day) keys already reported by this process, for `trigger: 'automatic'` writers. */
const reportedAutomatic = new Set<string>()

const sessionOrganizationId = (): string | null => {
  try {
    // Absent in some test doubles of AtomsProvider; the guard keeps the call total.
    if (!organizationIdAtom) return null
    const id: unknown = getDefaultStore().get(organizationIdAtom)
    return typeof id === 'string' && id !== '' ? id : null
  } catch {
    return null
  }
}

/**
 * Record that `writer` is about to write, for `organizationId` (defaults to the signed-in session's
 * organization). Call it immediately before the write, after any guard that skips the write.
 *
 * @returns whether an event was captured (false for a de-duplicated automatic writer, or on failure).
 */
export const reportFirestoreOnlyWriterUsage = (
  writer: FirestoreOnlyWriterName,
  /**
   * The organization the write targets, when the writer holds it (its `db` handle's id, an explicit
   * parameter). `unknown` because most writers hold an untyped Firestore handle: anything but a
   * non-empty string falls back to the session's organization rather than being sent as-is.
   */
  organizationId?: unknown,
  options: { target?: string } = {},
): boolean => {
  try {
    const entry: FirestoreOnlyWriterEntry = FIRESTORE_ONLY_WRITERS[writer]
    const org =
      typeof organizationId === 'string' && organizationId !== '' ? organizationId : sessionOrganizationId()
    if (entry.trigger === 'automatic') {
      const key = `${writer}|${org ?? ''}|${new Date().toISOString().slice(0, 10)}`
      if (reportedAutomatic.has(key)) return false
      reportedAutomatic.add(key)
    }
    const properties: OperationalProperties = {
      writer,
      organization_id: org,
      writer_registry_version: FIRESTORE_ONLY_WRITER_REGISTRY_VERSION,
    }
    if (typeof options.target === 'string' && options.target !== '') properties.writer_target = options.target
    captureOperationalEvent(FIRESTORE_ONLY_WRITER_EVENT, properties)
    return true
  } catch {
    // Telemetry must never be able to stop the write it is describing.
    return false
  }
}

/** Test seam. */
export const resetFirestoreOnlyWriterUsageForTests = (): void => {
  reportedAutomatic.clear()
}
